import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
  alpha,
  Divider,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  BarChart,
  ShowChart,
  Leaderboard,
  TrackChanges,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const VolumeTab = ({ country, currentCountryData, isLoggedIn }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const volumeData = currentCountryData.volume?.tradingVolumeDistribution || [];

  // console.log("Cnt:", country);

  const handleLogin = () => {
    navigate("/login");
  };

  const handleRegister = () => {
    navigate("/register");
  };
  // This is a reusable component for the stat cards, defined here for clarity
  const StatCard = ({ title, value, subtitle, icon, color }) => (
    <Card
      sx={{
        minWidth: { xs: 280, md: 280 },
        borderRadius: 4,
        boxShadow: theme.shadows[2], // Lighter shadow for a modern feel
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[6],
        },
        display: "flex",
        alignItems: "center",
        p: 2,
        gap: 2,
        background: alpha(theme.palette.background.paper, 0.8), // Lighter background
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 56,
          height: 56,
          borderRadius: 3,
          backgroundColor: alpha(color, 0.15),
          color: color,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ color: color }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {subtitle}
        </Typography>
      </Box>
    </Card>
  );

  const getNumericVolume = (volume) => {
    if (!volume) return 0; // handle null/undefined
    const str = String(volume); // convert to string safely
    return parseInt(str.replace("T", ""), 10) || 0;
  };

  const stats = [
    {
      title: "Total Volume",
      value: currentCountryData.volume.totalVolume || "-",
      icon: <ShowChart fontSize="large" color="info" />,
      color: theme.palette.info.main,
      subtitle: "Across all materials",
    },
    {
      title: "Average Volume",
      value: currentCountryData.volume.averageVolume || "-",
      icon: <BarChart fontSize="large" color="primary" />,
      color: theme.palette.primary.main,
      subtitle: "Per material",
    },
    {
      title: "Leading Material",
      value: currentCountryData.volume.leadingMaterial || "-",
      icon: <Leaderboard fontSize="large" color="warning" />,
      color: theme.palette.warning.main,
      subtitle: "Highest volume traded",
    },
    {
      title: "Market Activity",
      value: currentCountryData.volume.marketActivity || "-",
      icon: <TrackChanges fontSize="large" color="success" />,
      color: theme.palette.success.main,
      subtitle: "Currently trading",
    },
  ];

  return (
    <Box sx={{ position: "relative" }}>
      {/* Main content with blur when not logged in */}
      <Box
        sx={{
          filter: !isLoggedIn ? "blur(4px)" : "none",
          pointerEvents: !isLoggedIn ? "none" : "auto",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 4 },
            background: `linear-gradient(180deg, ${alpha(
              theme.palette.grey[100],
              0.6
            )}, #fff)`,
            borderRadius: 4,
          }}
        >
          {/* HEADER SECTION (UNCHANGED) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 2,
              mb: 4,
              p: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.info.main,
                0.08
              )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
            }}
          >
            <BarChart sx={{ fontSize: 36, color: theme.palette.info.main }} />
            <Box>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight="bold"
                gutterBottom
              >
                Volume Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {country} Market • Trading volume insights
              </Typography>
            </Box>
          </Box>

          {/* STATS CARDS SECTION */}
          {stats.every((stat) => !stat.value || stat.value === "-") ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 150,
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                mb: 4,
              }}
            >
              No volume statistics available.
            </Box>
          ) : (
            <Grid container spacing={3} mb={4} justifyContent="center">
              {stats.map((stat, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <StatCard {...stat} />
                </Grid>
              ))}
            </Grid>
          )}

          <Divider sx={{ mb: 4 }} />

          {/*VOLUME DISTRIBUTION SECTION */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: theme.shadows[6],
              border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
            }}
          >
            <Box
              sx={{
                p: { xs: 2, md: 3 },
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.info.main,
                  0.1
                )} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.info.main,
                  0.2
                )}`,
              }}
            >
              <Typography
                variant={isMobile ? "body1" : "h6"}
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <BarChart
                  fontSize="medium"
                  sx={{ color: theme.palette.info.main }}
                />
                Trading Volume Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Real-time volume breakdown across all materials
              </Typography>
            </Box>
            {volumeData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 150,
                  color: theme.palette.text.secondary,
                  fontStyle: "italic",
                  p: 4,
                }}
              >
                No trading volume data available.
              </Box>
            ) : (
              <Grid
                container
                spacing={2}
                sx={{ p: { xs: 2, md: 4 } }}
                justifyContent="center"
              >
                {[...volumeData]
                  .sort(
                    (a, b) =>
                      getNumericVolume(b.volume) - getNumericVolume(a.volume)
                  )
                  .map((item, i) => {
                    // const totalVolume = volumeData.reduce(
                    //   (sum, d) => sum + getNumericVolume(d.volume),
                    //   0
                    // );
                    // // const volumePercent =
                    // //   (getNumericVolume(item.volume) / totalVolume) * 100;
                    const colors = [
                      theme.palette.info.main,
                      theme.palette.primary.main,
                      theme.palette.secondary.main,
                      theme.palette.success.main,
                      theme.palette.warning.main,
                      theme.palette.error.main,
                    ];
                    const color = colors[i % colors.length];

                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                        <Card
                          sx={{
                            minWidth: { xs: 250, md: 230 },
                            flexShrink: 0,
                            height: "100%",
                            borderRadius: 3,
                            boxShadow: theme.shadows[1],
                            border: `1px solid ${alpha(color, 0.1)}`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: theme.shadows[4],
                              transform: "translateY(-4px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: color,
                                  }}
                                />
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  {item.material}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={`#${i + 1}`}
                                sx={{
                                  backgroundColor: alpha(color, 0.2),
                                  color: color,
                                  fontWeight: "bold",
                                }}
                              />
                            </Box>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ color: color }}
                            >
                              {item.volume}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                            >
                              {item.percentage} of total
                            </Typography>
                            <Box
                              sx={{
                                width: "100%",
                                height: 8,
                                backgroundColor: alpha(color, 0.1),
                                borderRadius: 4,
                                mt: 1,
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  width: item.percentage,
                                  height: "100%",
                                  background: `linear-gradient(90deg, ${color} 0%, ${alpha(
                                    color,
                                    0.8
                                  )} 100%)`,
                                  transition: "width 1.5s ease-in-out",
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Card>
        </Box>
      </Box>
      {/* CTA Overlay if not logged in */}
      {!isLoggedIn && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.6),
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Please log in to access volume insights
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Log In Button */}
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                backgroundColor: "#F4C542",
                color: "black",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#E3AE27",
                },
              }}
              onClick={handleLogin}
            >
              Login
            </Button>

            {/* Register Button */}
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                backgroundColor: theme.palette.common.black,
                color: theme.palette.common.white,
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: theme.palette.grey[900],
                },
              }}
              onClick={handleRegister}
            >
              Register
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VolumeTab;
